import { AgentOverride } from '../../libs/models/AgentConfig';

/**
 * One column of the per-agent override table in AgentConfigPanel.
 */
export type AgentOverrideColumn = 'provider' | 'model' | 'reasoningEffort';

/**
 * Resolves a provider's default model id (its IsDefault model, else the first listed),
 * or undefined when the provider is unknown/unset.
 */
export type DefaultModelResolver = (providerId: string | undefined) => string | undefined;

/**
 * Merge a single-column change from the AgentConfigPanel picker into the agent's current override,
 * returning the next override, or undefined to clear the whole row.
 *
 * Each column's reset <Option> carries value="". That empty string is folded to undefined up front
 * (`selected`) so it reads as "no value": treating '' as a present value let one column's reset collapse
 * the whole row's override and silently drop the other two columns' selections (#2425).
 */
export function applyAgentOverrideChange(
    current: AgentOverride,
    column: AgentOverrideColumn,
    value: string | undefined,
    resolveDefaultModel: DefaultModelResolver,
): AgentOverride | undefined {
    const selected = value === '' ? undefined : value;

    switch (column) {
        case 'provider': {
            const providerChanged = selected !== current.provider;
            // On a provider change, refill the new provider's default model. Carrying the previous
            // provider's model — or, on reset, the base agent's (Azure) model id — would 400 as
            // not-in-roster for the effective provider (#2388). When the provider is reset but was
            // never set, the model is standalone (against the base provider) and survives untouched.
            const nextModel = providerChanged ? resolveDefaultModel(selected) : current.model;
            // Guard on the *surviving* model, not current.model: resetting a real provider also drops
            // the model it owns, so a set-but-about-to-be-dropped model must not keep an otherwise-empty
            // row alive as a phantom { provider: undefined, model: undefined } override (#2425).
            if (selected === undefined && !nextModel && !current.reasoningEffort) {
                return undefined;
            }
            return {
                ...current,
                provider: selected,
                model: nextModel,
            };
        }
        case 'model': {
            // Resetting the model to Default while a provider override stands refills that provider's default
            // model, not a provider-only override that 400s server-side as not-in-roster (#2388). But a stored
            // provider can outlive its roster (removed/emptied in config while the override persists in
            // localStorage), so its default no longer resolves — keeping the provider would emit that same
            // provider-only shape. Drop the provider when nothing refills, so the invariant holds by
            // construction, not by roster stability; the selectable-provider gates only block *choosing* such
            // a provider, never one already stored.
            const nextModel = selected ?? resolveDefaultModel(current.provider);
            const nextProvider = nextModel ? current.provider : undefined;
            if (!nextModel && !nextProvider && !current.reasoningEffort) {
                return undefined;
            }
            return { ...current, provider: nextProvider, model: nextModel };
        }
        case 'reasoningEffort': {
            if (selected === undefined && !current.provider && !current.model) {
                return undefined;
            }
            return { ...current, reasoningEffort: selected };
        }
        default: {
            // Compile-time exhaustiveness: assigning to `never` fails the build if a new column is added
            // without a case. Unreachable for the typed union at runtime; throw rather than let the
            // pre-fix `return exhaustive` dispatch the raw column name string as a bogus override.
            const exhaustive: never = column;
            throw new Error(`applyAgentOverrideChange: unhandled column "${String(exhaustive)}"`);
        }
    }
}
